import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, AbstractControl, FormControl, Validators } from "@angular/forms";
import { ActivatedRoute, Router, Data } from "@angular/router";
import { Subscription, forkJoin, BehaviorSubject, Observable, of } from "rxjs";
import { tap, map, finalize, switchMap, catchError } from "rxjs/operators";
import { CanDeactivateComponent } from "../../../../common/classes/can-deactivate.component";
import { AuthService } from "../../../../services/auth.service";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { IUploadFile } from '../../../../common/services/document-management.service';
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { SubHeader } from "../../../../core/models/sub-header.model";
import { DomainDefinitionService } from "../../../../builder/services/domain-definition.service";
import { DocumentManagementService } from "../../../../common/services/document-management.service";
import { OnBaseKeywordMappingSearchSpec, OnBaseMappedKeyword, OnBaseDocumentTypeProcessReference, OnBaseSubProcessName } from "../../../../common/models/onBaseIntegrationAndMapping.model";
import { PermitService } from "../../../../common/services/permit.service";
import { LandownerProgramStatus, LandownerPassthroughProgramApplication, PassthroughProgramContractor, PassthroughProgramFile, PassthroughProgramLandowner, LandownerProgramCompleteWellRequest, LandownerProgramCompleteWellResponse, PassthroughProgramInspector } from "../../../../common/models/permit.model";
import { LoaderService } from "../../../../common/services/loader.service";
import { SidePanelService } from '../../../../common/services/side-panel.service';
import { SidePanel } from '../../../../common/models/side-panel.model';



@Component({
    selector: 'app-complete-program',
    templateUrl: './complete-program.component.html',
    styleUrls: ['./complete-program.component.scss'],
    standalone: false
})
export class CompleteProgramComponent extends CanDeactivateComponent implements OnInit, OnDestroy {

    application: LandownerPassthroughProgramApplication;
    applicationId: string;
    onBaseDocumentTypes: OnBaseDocumentTypeProcessReference[] = [];

    loadingError: boolean = false;

    form: FormGroup;
    statuses = LandownerProgramStatus;
    subscription: Subscription = new Subscription();

    loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    private countySubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    private townshipSubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

    countyObservable: Observable<string[]> = this.countySubject.asObservable();
    townshipObservable: Observable<string[]> = this.townshipSubject.asObservable();

    plugCompleteDialog: Dialog = new Dialog("Application [XXX] has been marked plugged via the Landowner Pass-through Program.", [new DialogButton("OK", true, () => { this.home(); })]);
    withdrawCompleteDialog: Dialog = new Dialog("Application [XXX] has been withdrawn from the Landowner Pass-through Program.", [new DialogButton("OK", true, () => { this.home(); })]);
    notFoundDialog: Dialog = new Dialog("Application [XXX] or Well [YYY] was unable to be found.  Program status has not been changed.", [new DialogButton("OK", false, () => { return; })]);

    showEmergencySection: boolean = false;
    showContractorSection: boolean = false;
    showDocumentationSection: boolean = false;
    showInspectorSection: boolean = false;
    showLandownerSection: boolean = true;
    showWellInformationSection: boolean = false;
    showWellLocationSection: boolean = false;

    finalStatuses: any = [
        { id: "", text: "--" },
        { id: LandownerProgramStatus.Plugged, text: "Well has been plugged" },
        { id: LandownerProgramStatus.Withdrawn, text: "Withdraw application" }
    ];

    states: any[] = [];
    private subheader: SubHeader = new SubHeader({ title: "Landowner Pass-through Payment Program - Application #[XXX]", buttons: [{ name: "View Timeline & Comments", method: () => this.showTimeline() } ] });
    watersheds: any[] = [];

    get dateOfLastProduction() : Date {
        if (this.application == null || this.application.passthroughProgramWell == null || this.application.passthroughProgramWell.dateOfLastProduction == null) return null;
        return new Date(this.application.passthroughProgramWell.dateOfLastProduction);
    }

    get contractorAddressForm(): FormGroup {
        if (this.form == null) return null;
        return this.form.get("contractorAddress") as FormGroup;
    }

    get emergencyDataForm(): FormGroup {
        if (this.form == null) return null;
        return this.form.get("emergencyData") as FormGroup;
    }

    get inspectorForm(): FormGroup {
        if (this.form == null) return null;
        return this.form.get("inspectors") as FormGroup;
    }

    get landownerAddressForm(): FormGroup {
        if (this.form == null) return null;
        return this.form.get("landownerAddress") as FormGroup;
    }

    get wellDataForm(): FormGroup {
        if (this.form == null) return null;
        return this.form.get("wellData") as FormGroup;
    }

    affidavitString: string = "Proposed Contractor's Affidavit of Insurance and Bonding";

    get affidavitCtrl(): AbstractControl {
        let ctrl: AbstractControl = new FormControl();
        const documentsCtrl = this.form.get("documents");
        if (documentsCtrl != null) {
            ctrl = documentsCtrl.get("affidavits") as AbstractControl;
        }
        return ctrl
    }

    get affidavitFiles(): IUploadFile[] {
        let files: IUploadFile[] = [];
        if (this.affidavitCtrl != null) {
            files = <IUploadFile[]>this.affidavitCtrl.value;
        }
        return files;
    }

    get applicationCtrl(): AbstractControl {
        let ctrl: AbstractControl = new FormControl();
        const documentsCtrl = this.form.get("documents");
        if (documentsCtrl != null) {
            ctrl = documentsCtrl.get("applications") as AbstractControl;
        }
        return ctrl
    }

    get applicationFiles(): IUploadFile[] {
        let files: IUploadFile[] = [];
        if (this.applicationCtrl != null) {
            files = <IUploadFile[]>this.applicationCtrl.value;
        }
        return files;
    }

    get contractorCtrl(): AbstractControl {
        let ctrl: AbstractControl = new FormControl();
        const documentsCtrl = this.form.get("documents");
        if (documentsCtrl != null) {
            ctrl = documentsCtrl.get("contractorFiles") as AbstractControl;
        }
        return ctrl
    }

    get contractorFiles(): IUploadFile[] {
        let files: IUploadFile[] = [];
        if (this.contractorCtrl != null) {
            files = <IUploadFile[]>this.contractorCtrl.value;
        }
        return files;
    }

    get mapCtrl(): AbstractControl {
        let ctrl: AbstractControl = new FormControl();
        const documentsCtrl = this.form.get("documents");
        if (documentsCtrl != null) {
            ctrl = documentsCtrl.get("maps") as AbstractControl;
        }
        return ctrl
    }

    get mapFiles(): IUploadFile[] {
        let files: IUploadFile[] = [];
        if (this.mapCtrl != null) {
            files = <IUploadFile[]>this.mapCtrl.value;
        }
        return files;
    }

    get packetsCtrl(): AbstractControl {
        let ctrl: AbstractControl = new FormControl();
        const documentsCtrl = this.form.get("documents");
        if (documentsCtrl != null) {
            ctrl = documentsCtrl.get("packets") as AbstractControl;
        }
        return ctrl
    }

    get packetFiles(): IUploadFile[] {
        let files: IUploadFile[] = [];
        if (this.packetsCtrl != null) {
            files = <IUploadFile[]>this.packetsCtrl.value;
        }
        return files;
    }

    get plugCtrl(): AbstractControl {
        let ctrl: AbstractControl = new FormControl();
        const documentsCtrl = this.form.get("documents");
        if (documentsCtrl != null) {
            ctrl = documentsCtrl.get("plugPlans") as AbstractControl;
        }
        return ctrl
    }

    get plugFiles(): IUploadFile[] {
        let files: IUploadFile[] = [];
        if (this.plugCtrl != null) {
            files = <IUploadFile[]>this.plugCtrl.value;
        }
        return files;
    }

    get restorationCtrl(): AbstractControl {
        let ctrl: AbstractControl = new FormControl();
        const documentsCtrl = this.form.get("documents");
        if (documentsCtrl != null) {
            ctrl = documentsCtrl.get("restorationPlans") as AbstractControl;
        }
        return ctrl
    }

    get restorationFiles(): IUploadFile[] {
        let files: IUploadFile[] = [];
        if (this.restorationCtrl != null) {
            files = <IUploadFile[]>this.restorationCtrl.value;
        }
        return files;
    }

    sidePanel: SidePanel = new SidePanel({
        tabs: [{ title: "Timeline" }, { title: "Comments" }],
        sections: [{ componentName: 'ProgramTimeline', componentData: [] }, { componentName: "ProgramNotes", componentData: {} }]
    });

    constructor(dialog: DynamicDialogService, auth: AuthService, private builder: FormBuilder, private subheaderService: SubHeaderService, private permitService: PermitService, public route: ActivatedRoute, public router: Router,
        private domainService: DomainDefinitionService, private documentService: DocumentManagementService, private loader: LoaderService, private sidePanelService: SidePanelService) {
        super(dialog, auth);
    }

    ngOnInit() {
        this.applicationId = this.route.snapshot.params.id;
        if (this.applicationId != null && this.applicationId.length > 0) {
            this.subheader.title = this.subheader.title.replace("[XXX]", this.applicationId);
        } else {
            this.subheader.title = this.subheader.title.replace("[XXX]", "");
        }
        this.subheaderService.updateSubHeader(this.subheader);
        this.confirmDialog = new Dialog("Any unsubmitted changes will be lost upon leaving this page.  Do you wish to continue?", [new DialogButton("Yes", true, () => { this.allowDeactivate.next(true); }), new DialogButton("No", false, () => { return false; })])
        this.allowDeactivate.next(true);
       
        const stateObs$: Observable<any> = this.domainService.getValues("STATE").pipe(
            tap((data: any) => {
                data.forEach(d => {
                    const obj = { Code: d.Code, Definition: d.Definition };
                    this.states.push(obj);
                })
            })
        )
        this.watersheds.push({ Code: "", Definition: "Not Provided" });
        const watershedObs$: Observable<any> = this.domainService.getValues("WATERSHED").pipe(
            tap((data: any) => {
                data.forEach(d => {
                    const obj = { Code: d.Code, Definition: d.Definition };
                    this.watersheds.push(obj);
                })
            })
        )
        const county$ = this.documentService.findKeywordMappings({ keywordName: "DOGRM - County" } as OnBaseKeywordMappingSearchSpec)
            .pipe(
                map((keys: OnBaseMappedKeyword[]) => {
                    let options: string[] = [];
                    keys.forEach(k => { k.dropdownValues.forEach(v => { options.push(v.value.toLocaleUpperCase()); }); });
                    options = options.filter(this.onlyUnique);
                    return options;
                }),
                tap((counties: string[]) => {
                    counties.unshift("--");
                    this.countySubject.next(counties);
                })
            );

        const township$ = this.documentService.findKeywordMappings({ keywordName: "DOGRM - Township" } as OnBaseKeywordMappingSearchSpec)
            .pipe(
                map((keys: OnBaseMappedKeyword[]) => {
                    let options: string[] = [];
                    keys.forEach(k => { k.dropdownValues.forEach(v => { options.push(v.value.toLocaleUpperCase()); }); });
                    options = options.filter(this.onlyUnique);
                    return options;
                }),
                tap((townships: string[]) => {
                    townships.unshift("--");
                    this.townshipSubject.next(townships);
                })
            );


        let dataSubs$: Observable<LandownerPassthroughProgramApplication> = this.route.data.pipe(
            map((data: Data) => {
                this.application = data.application;
                this.onBaseDocumentTypes = <OnBaseDocumentTypeProcessReference[]>data.documentTypes;
                return this.application;
            }));

         const subs$ = forkJoin(stateObs$, watershedObs$, county$, township$).pipe(
            switchMap(() => {
                return dataSubs$;
            }),
            catchError((error) => {
                console.error(error);
                this.loadingError = true;
                this.dialogService.showError();
                return of(null);
            }),
        ).subscribe((data: LandownerPassthroughProgramApplication) => {
                if (data != null) {
                    this.form = this.buildForm();
                    this.loading.next(false);
                }
            }, (error) => {
                console.error(error);
                this.dialogService.showError();
            }
            );        
        this.subscription.add(subs$);


    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }


    private buildAddressForm(obj: PassthroughProgramContractor | PassthroughProgramLandowner): FormGroup {
        return this.builder.group({
            street: [{ value: obj.streetAddress, disabled: true }, []],
            suite: [{ value: obj.suite, disabled: true }, []],
            poBox: [{ value: obj.postOfficeBox, disabled: true }, []],
            city: [{ value: obj.city, disabled: true }, []],
            state: [{ value: obj.stateCode, disabled: true }, []],
            zip: [{ value: obj.zipCode, disabled: true }, []]
        });
    }

    private documentTypeName(subProcessName: OnBaseSubProcessName): string {
        return this.onBaseDocumentTypes.find(d => d.subProcessName == subProcessName)?.onBaseDocumentTypeName;
    }

    private buildDocumentForm(): FormGroup {
        const storedAffidavits = this.application.passthroughProgramFiles.filter(f => f.documentType === this.documentTypeName(OnBaseSubProcessName.InsuranceAndBondingAffidavit));
        const affidavitFiles: IUploadFile[] = [];
        storedAffidavits.forEach(s => { affidavitFiles.push(this.convertFile(s)); });
        const storedApplications = this.application.passthroughProgramFiles.filter(f => f.documentType === this.documentTypeName(OnBaseSubProcessName.PluggingApplication));
        const applicationFiles: IUploadFile[] = [];
        storedApplications.forEach(s => { applicationFiles.push(this.convertFile(s)); });
        const storedContractorFiles = this.application.passthroughProgramFiles.filter(f => f.documentType === this.documentTypeName(OnBaseSubProcessName.ScopeOfWork));
        const contractorFiles: IUploadFile[] = [];
        storedContractorFiles.forEach(s => { contractorFiles.push(this.convertFile(s)); });
        const storedMaps = this.application.passthroughProgramFiles.filter(f => f.documentType === this.documentTypeName(OnBaseSubProcessName.LocationMap));
        const mapFiles: IUploadFile[] = [];
        storedMaps.forEach(s => { mapFiles.push(this.convertFile(s)); });
        const storedPackets = this.application.passthroughProgramFiles.filter(f => f.documentType === this.documentTypeName(OnBaseSubProcessName.ApplicationPacketReceived));
        const packetFiles: IUploadFile[] = [];
        storedPackets.forEach(s => { packetFiles.push(this.convertFile(s)); });
        const storedPlugFiles = this.application.passthroughProgramFiles.filter(f => f.documentType === this.documentTypeName(OnBaseSubProcessName.PluggingPlan));
        const plugFiles: IUploadFile[] = [];
        storedPlugFiles.forEach(s => { plugFiles.push(this.convertFile(s)); });
        const storedRestorationFiles = this.application.passthroughProgramFiles.filter(f => f.documentType === this.documentTypeName(OnBaseSubProcessName.RestorationAgreement));
        const restorationFiles: IUploadFile[] = [];
        storedRestorationFiles.forEach(s => { restorationFiles.push(this.convertFile(s)); });


        return this.builder.group({
            packets: [{ value: packetFiles, disabled: true }, []],
            applications: [{ value: applicationFiles, disabled: true }, []],
            contractorFiles: [{ value: contractorFiles, disabled: true }, []],
            maps: [{ value: mapFiles, disabled: true }, []],
            affidavits: [{ value: affidavitFiles, disabled: true }, []],
            plugPlans: [{ value: plugFiles, disabled: true }, []],
            restorationPlans: [{ value: restorationFiles, disabled: true }, []]
        });
    }

    private buildEmergencyContactForm(): FormGroup {
        return this.builder.group({
            firePhone: [{ value: this.application.passthroughProgramEmergencyData.firePhoneNumber, disabled: true }, []],
            fireExtension: [{ value: this.application.passthroughProgramEmergencyData.fireExtension, disabled: true }, []],
            medicalPhone: [{ value: this.application.passthroughProgramEmergencyData.medicalPhoneNumber, disabled: true }, []],
            medicalExtension: [{ value: this.application.passthroughProgramEmergencyData.medicalExtension, disabled: true }, []],
            ingress: [{ value: this.application.passthroughProgramEmergencyData.meansOfIngress, disabled: true }, []]
        });
    }

    private buildInspectorForm(): FormGroup {
        let inspector: PassthroughProgramInspector = this.application.passthroughProgramInspector;
        if (inspector == null) {
            inspector = {
                inspectorName: "",
                inspectorPhone: "",
                backupInspectorName: "",
                backupInspectorPhone: "",
                supervisorName: "",
                supervisorPhone: ""
            } as PassthroughProgramInspector;
        }
        return this.builder.group({
            inspectorName: [{ value: inspector.inspectorName, disabled: true }, []],
            inspectorPhone: [{ value: inspector.inspectorPhone, disabled: true }, []],
            backupInspectorName: [{ value: inspector.backupInspectorName, disabled: true }, []],
            backupInspectorPhone: [{ value: inspector.backupInspectorPhone, disabled: true }, []],
            supervisorName: [{ value: inspector.supervisorName, disabled: true }, []],
            supervisorPhone: [{ value: inspector.supervisorPhone, disabled: true }, []]
        });

    }


    private buildForm(): FormGroup {
        return this.builder.group({
            applicationId: [{ value: this.application.applicationId, disabled: true}],
            api: [{ value: this.application.wellId, disabled: true }, []],
            landownerName: [{ value: this.application.passthroughProgramLandowner.ownerName, disabled: true }, []],
            landownerAddress: this.buildAddressForm(this.application.passthroughProgramLandowner),
            landownerPhone: [{ value: this.application.passthroughProgramLandowner.contactPhone, disabled: true }, []],
            landownerEmail: [{ value: this.application.passthroughProgramLandowner.emailAddress, disabled: true }, []],
            contractorName: [{ value: this.application.passthroughProgramContractor.contractorName, disabled: true }, []],
            contractorRepresentative: [{ value: this.application.passthroughProgramContractor.contractorRepresentative, disabled: true }, []],
            contractorEstimate: [{ value: this.application.contractorEstimate, disabled: true }, []],
            contractorAddress: this.buildAddressForm(this.application.passthroughProgramContractor),
            contractorOfficePhone: [{ value: this.application.passthroughProgramContractor.officePhone, disabled: true }, []],
            contractorCellPhone: [{ value: this.application.passthroughProgramContractor.cellPhone, disabled: true }, []],
            contractorEmail: [{ value: this.application.passthroughProgramContractor.emailAddress, disabled: true }, []],
            wellData: this.buildWellForm(),
            emergencyData: this.buildEmergencyContactForm(),
            documents: this.buildDocumentForm(),
            inspectors: this.buildInspectorForm(),
            decision: ["", [Validators.required]],
            pluggedDate: ["", []]
        });
    }

    private buildWellForm(): FormGroup {
        return this.builder.group({
            latitude: [{ value: this.application.passthroughProgramWell.latitude, disabled: true }, []],
            longitude: [{ value: this.application.passthroughProgramWell.longitude, disabled: true }, []],
            county: [{ value: this.application.passthroughProgramWell.county, disabled: true }, []],
            township: [{ value: this.application.passthroughProgramWell.township, disabled: true }, []],
            section: [{ value: this.application.passthroughProgramWell.section, disabled: true }, []],
            lot: [{ value: this.application.passthroughProgramWell.lot, disabled: true }, []],
            tract: [{ value: this.application.passthroughProgramWell.tract, disabled: true }, []],
            urbanized: [{ value: this.application.passthroughProgramWell.urbanized ? "Y" : "N", disabled: true }, []],
            name: [{ value: this.application.passthroughProgramWell.wellName, disabled: true }, []],
            number: [{ value: this.application.passthroughProgramWell.wellNumber, disabled: true }, []],
            api: [{ value: this.application.passthroughProgramWell.wellId, disabled: true }, []],
            lastOwner: [{ value: this.application.passthroughProgramWell.lastOwner, disabled: true }, []],
            dateOfLastProduction: [{ value: this.application.passthroughProgramWell.dateOfLastProduction, disabled: true }, []],
            amountLastOil: [{ value: this.application.passthroughProgramWell.amountOfLastOil, disabled: true }, []],
            amountLastGas: [{ value: this.application.passthroughProgramWell.amountOfLastGas, disabled: true }, []],
            depthPlugged: [{ value: this.application.passthroughProgramWell.depthToBePlugged, disabled: true }, []],
            waterSource: [{ value: this.application.passthroughProgramWell.waterSource, disabled: true }, []],
            watershed: [{ value: this.application.passthroughProgramWell.watershed, disabled: true }, []],
            amountWater: [{ value: this.application.passthroughProgramWell.amountWater, disabled: true }, []]
        });
    }

    convertFile(data: PassthroughProgramFile): IUploadFile  {
        return {
            fileName: data.fileName,
            programArea: "",
            type: data.contentType,
            documentType: data.documentType,
            documentGroup: "DNR - OG - LANDOWNER PASS-THROUGH PROGRAM",
            blobUri: data.documentUri,
            rawFile: null,
            completed: true,
            isUploading: false,
            path: null,
            percentage: 100
        } as IUploadFile;
    }

    decisionChanged(value: any): void {        
        this.allowDeactivate.next(value != LandownerProgramStatus.Plugged && value != LandownerProgramStatus.Withdrawn);
        if (value == LandownerProgramStatus.Plugged) {
            this.form.get("pluggedDate").setValidators(Validators.required);
        } else {
            this.form.get("pluggedDate").clearValidators();
            this.form.get("pluggedDate").setValue("");
        }
        this.form.get("pluggedDate").updateValueAndValidity();
    }

    home(): void {
        this.allowDeactivate.next(true);
        this.router.navigate(["/home"]);
    }

    onlyUnique(value, index, self) {
        return self.indexOf(value) === index;
    }

    pluggedDateChanged(evt: Date): void {
        this.form.get("pluggedDate").setValue(evt);
    }

    showTimeline(): void {
        this.sidePanel.sections[0].componentData = this.application.landownerPassthroughProgramHistories;
        this.sidePanel.sections[1].componentData = { applicationId: this.application.applicationId, notes: this.application.landownerPassthroughProgramNotes };
        this.sidePanelService.newSidePanel(this.sidePanel);
        this.sidePanelService.updateSectionData("ProgramTimeline", this.application.landownerPassthroughProgramHistories)
        this.sidePanelService.updateSectionData("ProgramNotes", { applicationId: this.application.applicationId, notes: this.application.landownerPassthroughProgramNotes });
        this.sidePanelService.toggleSidePanel(true);
    }

    submit(): void {
        if (this.form.valid) {
            let status = this.form.get("decision").value as LandownerProgramStatus;
            let request = {
                applicationId: this.applicationId,
                wellId: this.application.wellId,
                statusUpdate: status,
                pluggedDate: (status == LandownerProgramStatus.Plugged) ? this.form.get("pluggedDate").value : null
            } as LandownerProgramCompleteWellRequest;

            this.loader.toggleLoader(true);
            const updateSubs$ = this.permitService.completeLandownerPassthroughApplication(request).pipe(
                finalize(() => { this.loader.toggleLoader(false); })
            ).subscribe(
                (response: LandownerProgramCompleteWellResponse) => {
                    let dialog: Dialog;
                    switch (response.status) {
                        case LandownerProgramStatus.Plugged:
                            dialog = this.plugCompleteDialog;
                            break;
                        case LandownerProgramStatus.Withdrawn:
                            dialog = this.withdrawCompleteDialog;
                            break;
                        default:
                            dialog = this.notFoundDialog;
                            break;
                    }
                    if (dialog != null) {
                        dialog.message = dialog.message.replace("[XXX]", this.applicationId);
                        dialog.message = dialog.message.replace("[YYY]", this.application.wellId);
                        dialog.toggleDialog(true);
                        this.dialogService.updateDialog(dialog);
                    }
                },
                (error) => {
                    console.error(error);
                    this.dialogService.showError();
                }
            );
            this.subscription.add(updateSubs$);
        }
    }

    toggleSection(sectionName: string): void {
        switch (sectionName) {
            case "landowner":
                this.showLandownerSection = !this.showLandownerSection
                break;
            case "contractor":
                this.showContractorSection = !this.showContractorSection;
                break;
            case "wellInformation":
                this.showWellInformationSection = !this.showWellInformationSection;
                break;
            case "wellLocation":
                this.showWellLocationSection = !this.showWellLocationSection;
                break;
            case "emergency":
                this.showEmergencySection = !this.showEmergencySection;
                break;
            case "documentation":
                this.showDocumentationSection = !this.showDocumentationSection;
                break;
            case "inspector":
                this.showInspectorSection = !this.showInspectorSection;
                break;
        }
    }
}
